import type { ReactNode } from "react";
import { Badge, Checkbox, cn } from "@eduviaos/ui-kit";

import type { SelectableNode } from "../lib/collectNodes.js";

/**
 * Presentational row for one selectable diff node (Phase F-finale-4 Slice 5).
 *
 * Owns the per-node checkbox + the validity / module-gate badges so every
 * diff renderer (requirement / field / stage / funnel) presents nodes
 * identically. Invalid nodes render a disabled checkbox + a destructive-toned
 * reason line; module-gated nodes render a "Needs <module>" badge.
 *
 * Animation: the only motion is the checkbox's `transition-colors` (from
 * ui-kit) — no row transform/scale, honouring Iron Rule #6 / #16 for dense
 * review lists.
 */
export interface DiffNodeRowProps {
  node: SelectableNode;
  checked: boolean;
  onToggle: () => void;
  /** Accessible name for the checkbox (plain text). */
  ariaLabel: string;
  title: ReactNode;
  /** One-line LLM rationale. */
  reason: string;
  /** Kind-specific detail rows (config chips, field meta, etc.). */
  details?: ReactNode;
  className?: string;
}

export function DiffNodeRow({
  node,
  checked,
  onToggle,
  ariaLabel,
  title,
  reason,
  details,
  className,
}: DiffNodeRowProps) {
  const blocked = !node.isValid;
  return (
    <div className={cn("flex gap-3 py-2", className)} role="group">
      <Checkbox
        className="mt-0.5"
        aria-label={ariaLabel}
        checked={checked && node.isValid}
        disabled={blocked}
        onCheckedChange={() => onToggle()}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("text-sm font-medium", blocked && "text-muted-foreground")}>
            {title}
          </span>
          {node.requiresModule ? <Badge variant="warn">Needs: {node.requiresModule}</Badge> : null}
          {blocked && !node.requiresModule ? <Badge variant="danger">Unavailable</Badge> : null}
        </div>
        <p className="text-muted-foreground text-xs">{reason}</p>
        {blocked && node.invalidReason ? (
          <p className="text-destructive text-xs">{node.invalidReason}</p>
        ) : null}
        {details}
      </div>
    </div>
  );
}
